import { LoadedData, useLoadedData } from "../../hooks/useLoadedData";
import { apiFetch, apiUploadFile, downloadFile as apiDownloadFile } from "../../api/core";
import { WithTextFilter } from "../schemed/Filtering/useTextFilter";
import React, { useEffect, useState } from "react";
import { useAction } from "../../api/useAction";
import { useAutoSuggestionsStore } from "../primitives/AutoSuggests";

export interface MediaFile {
    space_id: string; 
    _id: string; 
    filename: string; 
    thumbnail?: string | null; 
    contenttype: string; 
    created_at: string; 
    fs_path?: string;
    tags?: string[] | null;
}

export interface MediaLib extends Omit<LoadedData<MediaFile[]>, "setData">, WithTextFilter {
    uploadFile: (f: File) => Promise<MediaFile>;
    removeFile: (mf: MediaFile) => Promise<MediaFile>;
    updateFileInfo: (mf: MediaFile, changes: Pick<MediaFile, "tags">) => Promise<MediaFile>;
    replaceFile: (mf: MediaFile, file: File) => Promise<MediaFile>;
    downloadFile: (mf: MediaFile) => void;
    exportAll: () => void;
    getFilepath: (mf: MediaFile) => string;
    isUploading: boolean;
    protectedFiles: boolean;

    requestLoad?: () => void;
    tags?: {
      available: string[],
      filter: string[],
      setFilter: React.Dispatch<React.SetStateAction<string[]>>,
    };
}

export interface MediaLibConfig {
    noLoad?: boolean;
    loadOnDemand?: boolean;
    noReloadOnUpload?: boolean;
    getFilepath?: (mf: MediaFile) => string;
    urlPrefix?: string;
    protectedFiles?: boolean;
}

export const MediaLibTagsSuggestionsKey = "medialib_tags_suggestions";

export const useMediaLib = (apiPath: string, spaceId: string, cfg?: MediaLibConfig): MediaLib => {
    const apiPathX = `${apiPath}/${spaceId}`;
    const [loadRequested,setLoadRequested] = useState<boolean>(false);
    const [tagsFilter, setTagsFilter] = useState<string[]>([]);
    const tagsFilterText = (tagsFilter || []).join(",");
    const data = useLoadedData<MediaFile[]>(`${apiPathX}${tagsFilterText ? `?tags=${tagsFilterText}` : ""}`, [], !!spaceId && !cfg?.noLoad && (!cfg?.loadOnDemand || loadRequested));
    const [filter, setFilter] = useState<string>("");

    const tags = useLoadedData<{ all: string[]}>(`${apiPathX}/tags`, { all: [] }, !!spaceId && !cfg?.noLoad && (!cfg?.loadOnDemand || loadRequested));
    const autosuggestions = useAutoSuggestionsStore();
    useEffect(() => {
      autosuggestions.setOptions(MediaLibTagsSuggestionsKey, tags.data?.all || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tags.data])
    
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const getFilepath = cfg?.getFilepath || (cfg?.urlPrefix
        ? (mf => `${cfg.urlPrefix}/${mf.space_id}/${mf.filename}`)
        : (mf => `/uploads/${mf.space_id}/${mf.filename}`));

    const fileUploadOp = <T,>(op: Promise<T>, noReload?: boolean) => {
        setIsUploading(true);
        return op.then(x => {
            if(!(noReload || cfg?.noReloadOnUpload)) {
                data.reload();
            }
            setIsUploading(false);
            return x;
        })
        .catch(e => {
            setIsUploading(false);
            throw e;
        })
    };

    const uploadFile = (f: File) => fileUploadOp(
        apiUploadFile(tagsFilterText ? `${apiPathX}?tags=${tagsFilterText}` : apiPathX, "post", "file", f).then(x => x.data as MediaFile));

    const updateFileInfo = async (mf: MediaFile, changes: Pick<MediaFile, "tags">) => {
      const result = await apiFetch<MediaFile>(`${apiPathX}/${mf._id}/info`, "put", changes);
      data.setData(x => x.map(f => f._id === mf._id ? { ...f, tags: result.tags } : f));
      tags.reload();
      return result;
    }

    const replaceFile = (mf: MediaFile, f: File) => fileUploadOp(
        apiUploadFile(`${apiPathX}/${mf._id}`, "put", "file", f).then(x => x.data as MediaFile));

    const removeFile = (mf: MediaFile) => fileUploadOp(
        apiFetch<MediaFile>(`${apiPathX}/${mf._id}`, "delete")
            .then(x => {
                data.setData(data.data.filter(mf2 => mf2._id !== mf._id));
                return x;
            }),
            true);

    const downloadFile = (mf: MediaFile) => {
        apiDownloadFile(getFilepath(mf), mf.filename);
    }

    const exportAll = useAction(() => apiDownloadFile(`${apiPathX}/export`, `Medialib ${spaceId} export.zip`), !!spaceId);

    const filteredItems = filter ? data.data.filter(mf => mf.filename.toLowerCase().includes(filter.toLowerCase())) : data.data;

    return {
        ...data,
        isLoading: data.isLoading || exportAll.isRunning,
        data: filteredItems,
        uploadFile,
        updateFileInfo,
        replaceFile,
        removeFile,
        downloadFile,
        exportAll: exportAll.run,
        getFilepath,
        filter,
        setFilter,
        isUploading,
        protectedFiles: !!cfg?.protectedFiles,
        requestLoad: () => setLoadRequested(true),
        tags: {
          available: tags.data?.all || [],
          filter: tagsFilter,
          setFilter: setTagsFilter,
        },
    }
}